import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Copy, ChevronDown, ChevronUp } from 'lucide-react';

const PromptLibrary = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [expandedPrompts, setExpandedPrompts] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const mainCategories = [
     {
      id: 'applying-to-college',
      title: 'Applying To College',
      description: 'Guidance for college essays, applications, and admissions strategies',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      prompts: [
        {
          title: 'My Weaknesses',
          content: `Create a week-by-week work plan for my college applications.

TODAY'S DATE: [current date]

MY COLLEGE LIST:
- [School 1]: Deadline [date], supplements: [list prompts briefly]
- [School 2]: Deadline [date], supplements: [list prompts briefly]
[Continue for all schools]

WHAT I'VE COMPLETED:
- Common App essay: [done/in progress/not started]
- Activities list: [done/in progress/not started]
- Letters of rec: [requested/not requested]

MY WEEKLY AVAILABILITY: [X hours per week to work on apps]

Create a realistic weekly plan that:
1. Prioritizes earliest deadlines
2. Groups similar supplements together
3. Builds in buffer time for revisions
4. Includes specific tasks (e.g., "Draft Why Penn essay" not just "work on supplements")`,
          tags: ['supplemental', 'brainstorm', 'authentic']
        },
        {
          title: 'Admissions Committee Roleplay',
          content: `You are now three admissions officers reviewing my college application. Role-play a committee discussion about my candidacy:

Officer 1 - The Advocate: Enthusiastically supports my admission, highlights my strengths
Officer 2 - The Skeptic: Points out concerns and weaknesses in my application  
Officer 3 - The Neutral: Balanced perspective, asks clarifying questions

MY PROFILE:
- GPA (Weighted): [X.XX]
- GPA (Unweighted): [X.XX]
- SAT/ACT: [XXXX]
- AP/IB Courses: [list them]
- Extracurriculars: [list top 5-7 activities with brief descriptions]
- Awards/Honors: [list them]
- Intended Major: [major]
- Personal circumstances: [anything relevant - first gen, financial hardship, family responsibilities, etc.]
- Personal Statement [paste here]

SCHOOL I'M APPLYING TO: [School Name]

Have these three officers debate my application for 10 minutes. Then provide a summary vote and reasoning.`,
       
        },
        {
          title: 'Personalized College Match Finder',
          content: `Help me find colleges that align with my profile and preferences. For each school you recommend, give me 3 specific pros and 3 specific cons.

MY CRITERIA:
- Location preference: [city/suburban/rural, specific states or regions, distance from home]
- Cost to get home: [how many times do you want to visit? How long is the trek? How many times?]
- Climate preference: [warm/cold/mild/don't care]
- School size: [small <5k, medium 5-15k, large >15k, or flexible]
- Setting I thrive in: [describe - collaborative, competitive, quirky, traditional, outdoorsy, etc.]
- Budget/Financial: Family income is $[XXX,XXX], willing to pay $[XX,XXX] per year maximum
- Academic stats: [GPA weighted], [GPA unweighted], [SAT/ACT score]
- Intended major(s): [major]
- Must-haves: [Greek life, D1 sports, study abroad, research opportunities, specific programs, etc.]
- Deal-breakers: [religious affiliation, too far from city, no diversity, etc.]
- Career goals: [what you want to do after college]

Recommend 8-10 schools (mix of reach, target, safety) with 3 pros and 3 cons for each based specifically on MY profile and preferences.

`,
          
        },
        {
          title: 'Narrative Audit',
          content: `Analyze my entire college application and tell me what story it's telling. Then identify gaps, contradictions, and opportunities.

MY ACTIVITIES LIST:
1. [Activity name] - [role, years involved, hours/week, brief description]
2. [Continue for all activities]

MY AWARDS/HONORS:
[List them]

MY ESSAYS (paste each):
Common App Essay: [paste essay]

Supplement 1 for [School]: [paste essay]

Supplement 2 for [School]: [paste essay]

[Add more as needed]

QUESTIONS:
1. What 3-5 words would you use to describe me based on this application?
2. What is the central narrative/theme? Is it consistent?
3. What's missing from this story?
4. What seems contradictory or unclear?
5. What makes me unique compared to other applicants?
6. Where could I add more depth or authenticity?
          `
          ,
          
        },
        {
          title: 'Could Anyone Else Write This',
          content: `Review my college essay and tell me if it's truly unique to me.

MY ESSAY:
[paste your essay]

QUESTIONS:
1. Could 100 other applicants have written this if you removed my name? Be brutally honest.
2. What specific details make it uniquely mine?
3. Where am I using clichés or generic statements?
4. Which sentences could be more concrete, sensory, or specific?
5. Does my authentic voice come through, or does it sound like "college essay voice"?
6. Rewrite the 3 weakest sentences to be more specific and compelling.
          `
          ,
          tags: ['why-us', 'essay', 'research']
        },
        {
          title: 'Supplement Recycling Plan',
          content: `I need to write multiple college supplements efficiently. Help me create a strategy to reuse and adapt essays.

MY COLLEGE LIST & THEIR PROMPTS:

[School 1]:
- Prompt 1: [paste prompt, word count]
- Prompt 2: [paste prompt, word count]

[School 2]:
- Prompt 1: [paste prompt, word count]
- Prompt 2: [paste prompt, word count]

[Continue for all schools]

MY INTENDED MAJOR: [major]

CORE THEMES I WANT TO COVER: [e.g., my research experience, my cultural identity, my leadership in X, my passion for Y]

TASK:
1. Identify which prompts are asking similar questions
2. Create 3-4 "core essays" I can write and adapt for multiple schools
3. Map out which core essay fits which prompts
4. Tell me which essays I truly need to write from scratch
5. Give me a suggested writing order (tackle most important/earliest deadlines first)
`,
          tags: ['college-list', 'strategy', 'planning']
        },
        {
          title: 'My Weaknesses',
          content: `I have some weaknesses in my application. Help me find the most honest, compelling way to address them.

MY WEAKNESSES:
- [e.g., Low GPA freshman year, dropped a class, gap in extracurriculars, disciplinary issue, low test scores, etc.]
- [Context: explain what was happening - illness, family circumstances, learning disability, etc.]

MY STRENGTHS:
- [What I've done well despite challenges]
- [Upward trends or improvements]

TASKS:
1. What's the strongest, most authentic interpretation of my weaknesses?
2. Should I address this in Additional Information, in an essay, or not at all?
3. Write a draft explanation (if needed) that shows growth and maturity without making excuses
4. How can I reframe my narrative so the weakness is part of a larger story of resilience?
          `,
          tags: ['supplemental', 'brainstorm', 'authentic']
        },
        {
          title: 'Post-Acceptance Decision Helper',
          content: `I've been accepted to multiple schools and need help deciding. Interview me to help clarify my priorities.

SCHOOLS I GOT INTO:
- [School 1]: Cost after aid = $[XX,XXX]/year
- [School 2]: Cost after aid = $[XX,XXX]/year  
- [School 3]: Cost after aid = $[XX,XXX]/year

Ask me 15-20 questions about:
- My career goals
- What kind of college experience I want
- My learning style
- Financial comfort level and debt tolerance
- Post-grad plans
- What I value most in a community

After I answer, tell me which school aligns best with my stated priorities and which decision I'm least likely to regret in 10 years. Use the "Regret Minimization Framework."
          `,
          tags: ['supplemental', 'brainstorm', 'authentic']
        }
      ]
    },
    {
      id: 'emails',
      title: 'Emails',
      description: 'Professional email templates for various academic and professional situations',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      prompts: [
        {
          title: 'Email to Teacher',
          content: 'Write a concise, professional email to my teacher [Teacher Name] about [topic/question]. My name is [Your Name] and I am in your [Class Name/Period] class. I would like to [specific request: discuss a grade, ask for help, request a meeting, clarify an assignment, etc.]. Keep the email clear, respectful, and to the point - no more than 3-4 sentences.',
          tags: ['academic', 'formal', 'teacher']
        },
        {
          title: 'Research Opportunity Request',
          content: 'Write a concise email to [Professor/Researcher Name] expressing interest in their research on [topic]. My name is [Your Name], I am a [grade/year] student at [School], and I have relevant experience/skills in [list 2-3 key skills]. Explain in 1-2 sentences why their research interests me and ask if they have any opportunities available. Keep the tone professional and the email under 5 sentences total.',
          tags: ['research', 'opportunities', 'academic']
        },
        {
          title: 'Recommendation Letter Request',
          content: 'Write a clear, respectful email to [Teacher Name] requesting a letter of recommendation for [college/scholarship/program]. Remind them of 1-2 specific achievements or contributions in their class, provide the deadline of [Date], and include any relevant details they might need (e.g., where to submit, what the program is about). Keep the email concise and express genuine appreciation for their time and support.',
          tags: ['recommendation', 'formal', 'deadline']
        },
        {
          title: 'Scholarship Thank You',
          content: 'Write a sincere but concise thank you email to the [Scholarship Name] committee for awarding me the scholarship. Express gratitude, briefly mention (in 1 sentence) how the scholarship will help me achieve my academic goals at [University/in my studies], and keep the overall tone warm but professional. Maximum 4-5 sentences.',
          tags: ['scholarship', 'gratitude', 'formal']
        },
        {
          title: 'Absence or Late Work Explanation',
          content: 'Write a professional email to [Teacher Name] explaining [my absence on Date / why I need an extension on Assignment]. Keep it honest and concise. My reason is: [briefly state reason - illness, family emergency, etc.]. Ask politely about making up missed work or request a new deadline. Show that I take my responsibilities seriously. Maximum 4 sentences.',
          tags: ['absence', 'extension', 'accountability']
        }
      ]
    },
  
    {
      id: 'extracurricular',
      title: 'Extracurricular',
      description: 'Tools for running clubs, organizations, and student ventures',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      prompts: [
        {
          title: 'Creative Logo Designer',
          content: `Design a unique, memorable logo for my [club/organization/business/project].

**PROJECT NAME:**
[Your name/what you want to call it]

**WHAT WE DO:**
[Describe your activity/mission in 2-3 sentences]

**TARGET AUDIENCE:**
[Who is this for? Students? Community members? Specific age group?]

**BRAND PERSONALITY:**
[Choose 3-5 words: professional, playful, serious, creative, tech-forward, traditional, bold, minimal, friendly, edgy, academic, etc.]

**VISUAL PREFERENCES:**
- Style I like: [modern/vintage/hand-drawn/geometric/organic/etc.]
- Colors I'm thinking: [list 2-3 colors or say "surprise me with a palette"]
- Symbols/imagery that represent us: [e.g., for a book club: not just a book, maybe an open door, a light bulb, intertwined paths representing different stories]

**WHAT MAKES US DIFFERENT:**
[What's unique about your organization that should be reflected?]

**AVOID:**
[Any cliches or overused imagery to avoid - e.g., "don't just use a generic cupcake for a baking club"]

---

Generate 3 completely different logo concepts. For each:
1. Describe the visual design in detail (shapes, layout, style)
2. Suggest a specific color palette with hex codes
3. Explain the symbolism and why it works for our brand
4. Describe what fonts/typography would complement it
5. Show how it would look in different contexts (social media profile pic, t-shirt, flyer header)

Make the concepts creative and NOT obvious. Push beyond the first idea that comes to mind.`,
          tags: ['branding', 'visual', 'creative']
        },
        {
          title: 'LLC Setup Guide',
          content: `I want to set up an LLC for my [business type/project]. Create a comprehensive checklist and guide for me.

**MY BUSINESS:**
[Brief description of what the business does]

**STATE:**
[Which US state - laws vary by state]

**NUMBER OF OWNERS:**
[Just me / partnership with X people]

---

Provide:

**STEP-BY-STEP SETUP PROCESS:**
1. Pre-formation decisions (single-member vs multi-member, operating agreement needs, etc.)
2. Naming requirements and how to check availability
3. Articles of Organization filing process
4. EIN (Employer Identification Number) application
5. Operating Agreement essentials (even if not legally required)
6. State-specific requirements for [STATE]
7. Business licenses and permits needed for [business type]
8. Bank account setup requirements
9. Ongoing compliance (annual reports, taxes, etc.)

**ESTIMATED COSTS:**
- Filing fees
- Annual fees
- Other typical costs

**TIMELINE:**
How long each step typically takes

**IMPORTANT DEADLINES:**
What needs to be done by when

**COMMON MISTAKES TO AVOID:**
List 5 pitfalls specific to student/young entrepreneurs

**RESOURCES:**
Where to file, helpful government websites, etc.

Keep explanations clear for someone doing this for the first time.`,
          tags: ['legal', 'business', 'LLC']
        },
        {
          title: 'Nonprofit Organization Setup',
          content: `I want to start a nonprofit organization for [cause/mission]. Guide me through the setup process.

**MISSION:**
[What social good will this organization accomplish?]

**ACTIVITIES:**
[What will the nonprofit actually do? Programs, services, etc.]

**STATE:**
[Which state will this be registered in?]

**SCOPE:**
[Local community? Statewide? National?]

---

Provide a complete roadmap:

**STEP 1: PLANNING PHASE**
- Defining mission and vision statements
- Identifying the need/gap you're filling
- Researching existing organizations (competition/collaboration opportunities)
- Determining if nonprofit status is right for this (vs. other structures)

**STEP 2: FORMATION**
- Choosing a name (requirements and availability check)
- Recruiting initial board of directors (who you need, roles, requirements)
- Drafting bylaws (key elements to include)
- Articles of Incorporation filing process
- EIN application

**STEP 3: 501(c)(3) TAX-EXEMPT STATUS**
- IRS Form 1023 vs 1023-EZ (which to use and why)
- Required documentation
- Timeline and fees
- What to expect during IRS review

**STEP 4: STATE-LEVEL REQUIREMENTS**
- State tax exemption
- Charitable solicitation registration
- Other state-specific requirements for [STATE]

**STEP 5: OPERATIONAL SETUP**
- Bank account
- Accounting systems
- Conflict of interest policies
- Donor management

**STEP 6: ONGOING COMPLIANCE**
- Annual Form 990 filing
- State annual reports
- Board meeting requirements
- Record-keeping obligations

**REALISTIC TIMELINE:**
How long does each phase take?

**STARTUP COSTS:**
What should I budget for?

**COMMON MISTAKES:**
What do first-time nonprofit founders mess up?

Make this accessible for a student/young person with limited legal/business experience.`,
          tags: ['nonprofit', 'legal', '501c3']
        },
        {
          title: 'Not-For-Profit Setup Guide',
          content: `I want to set up a not-for-profit organization for [purpose]. Explain the difference from a nonprofit and guide me through setup.

**PURPOSE:**
[What is this organization for?]

**ACTIVITIES:**
[What will it do? Social club? Professional association? Hobby group?]

**STATE:**
[Which state?]

---

**PART 1: NONPROFIT VS NOT-FOR-PROFIT**
Explain clearly:
- Key legal differences
- Tax implications of each
- Which structure fits my purpose better
- Can I switch later if needed?

**PART 2: FORMATION PROCESS**
1. Name selection and reservation
2. Articles of Incorporation/Organization
3. Bylaws creation (key provisions)
4. Board/member structure
5. EIN application
6. State registration

**PART 3: TAX CONSIDERATIONS**
- Which IRS tax-exempt category might apply (if any)
- State tax obligations
- Sales tax exemption (if applicable)
- When members' dues are/aren't taxable

**PART 4: OPERATIONAL STRUCTURE**
- Membership rules and dues
- Meeting requirements
- Voting procedures
- Financial management
- Record-keeping

**PART 5: COMPLIANCE**
- Annual state filings
- Financial reporting
- Meeting minutes requirements

**COSTS & TIMELINE:**
Breakdown of fees and how long each step takes

**SPECIFIC GUIDANCE FOR:**
[My state] laws and requirements

Keep this practical for someone new to organizational management.`,
          tags: ['not-for-profit', 'legal', 'organization']
        },
        {
          title: 'Fundraising Campaign Planner',
          content: `Help me plan a fundraising campaign for [organization/cause/project].

**WHAT WE NEED MONEY FOR:**
[Specific goal - equipment, event costs, program funding, etc.]

**FUNDRAISING GOAL:**
$[amount]

**DEADLINE:**
[Date we need the money by]

**OUR AUDIENCE:**
[Who will we ask? Students, parents, local businesses, alumni, community members?]

**RESOURCES WE HAVE:**
[Social media following, email list size, number of active members, any initial budget, etc.]

**PAST FUNDRAISING:**
[Have we done this before? What worked or didn't work?]

---

Create a complete fundraising strategy:

**RECOMMENDED FUNDRAISING MIX:**
Suggest 4-6 different fundraising methods based on our audience and goal. For each method:
- How it works
- Estimated potential revenue
- Time/effort required
- Resources needed
- Timeline

Examples: crowdfunding, sponsorships, fundraising events, donation drives, grant applications, merchandise sales, etc.

**DETAILED CAMPAIGN PLAN:**
- Week-by-week action plan leading up to deadline
- Key milestones
- Who needs to do what
- Communication schedule (when to post, email, etc.)

**STORYTELLING STRATEGY:**
- How to communicate our need compellingly
- Specific talking points for different audiences
- Sample social media posts (3 different angles)
- Email template for donation requests

**INCENTIVES & RECOGNITION:**
- Should we offer anything to donors?
- How to thank and recognize supporters
- Milestone celebrations

**TRACKING & ACCOUNTABILITY:**
- How to track donations
- Progress update strategy
- Pivot points (what if we're behind schedule?)

**LEGAL/ETHICAL CONSIDERATIONS:**
- Any rules or regulations we need to follow
- Transparency best practices
- How to handle money properly

Make this actionable with specific tasks we can start tomorrow.`,
          tags: ['fundraising', 'planning', 'finance']
        },
        {
          title: 'Social Media Content Calendar',
          content: `Create a 30-day social media content calendar for my [club/organization/project].

**ORGANIZATION:**
[Name and what we do]

**GOALS FOR SOCIAL MEDIA:**
[What do we want to achieve? More members? Event attendance? Awareness? Engagement?]

**PLATFORMS WE USE:**
[Instagram, TikTok, Twitter/X, LinkedIn, Facebook, etc.]

**POSTING FREQUENCY:**
[How often can we realistically post? Daily? 3x/week?]

**UPCOMING EVENTS/DEADLINES:**
[List any events, meetings, deadlines, or important dates in the next 30 days]

**CONTENT THEMES:**
[What topics should we cover? Educational? Behind-the-scenes? Member spotlights? Impact stories?]

**BRAND VOICE:**
[How should we sound? Professional? Fun? Inspirational? Casual?]

---

Create a detailed 30-day content calendar with:

**FOR EACH POST:**
- Date and time to post
- Platform(s)
- Content type (photo, video, carousel, story, reel, etc.)
- Caption/text (write the actual caption)
- Hashtags to use
- Visual suggestions (what to photograph/create)
- Call-to-action

**CONTENT MIX:**
Balance between:
- Educational/value-adding content
- Engagement/community building
- Promotional/recruitment
- Behind-the-scenes/humanizing
- User-generated content opportunities

**STRATEGIC ELEMENTS:**
- Best times to post for our audience
- Which posts should be boosted/promoted (if we have a small budget)
- Cross-platform promotion strategy
- Story/feed/reel strategy for Instagram/TikTok

**ENGAGEMENT PLAN:**
- When to respond to comments
- Community management tactics
- How to encourage shares and tags

**TEMPLATES:**
Provide 3-5 reusable caption templates we can adapt
Provide 3-5 post ideas we can repeat monthly with fresh content

Make this copy-paste ready so we can schedule everything in one sitting.`,
          tags: ['social-media', 'marketing', 'content']
        },
        {
          title: 'Event Planning Checklist',
          content: `Help me plan [event type] for [organization/purpose].

**EVENT DETAILS:**
- Event name: [name]
- Date: [date or "TBD - help me choose"]
- Expected attendance: [number]
- Budget: $[amount or "need help determining this"]
- Location: [venue or "need suggestions"]

**EVENT PURPOSE:**
[Why are we doing this? Fundraiser? Community building? Recruitment? Educational?]

**TARGET AUDIENCE:**
[Who is this for?]

**MUST-HAVES:**
[Any specific elements this event needs to include?]

---

Create a comprehensive event plan:

**TIMELINE: [X] WEEKS BEFORE EVENT**
Break down week-by-week what needs to be done, including:
- Initial planning and team formation
- Venue booking and permits
- Budget finalization
- Vendor/supplier outreach
- Marketing and promotion phases
- Ticket sales (if applicable)
- Final confirmations
- Day-of preparation
- Post-event follow-up

**DETAILED BUDGET BREAKDOWN:**
- Venue costs
- Supplies/materials
- Food/beverage
- Entertainment/speakers
- Marketing/printing
- Insurance/permits
- Contingency fund (10-15%)
- Potential revenue sources

**LOGISTICS CHECKLIST:**
Everything we need to secure, including:
- Venue and room setup
- AV equipment
- Tables, chairs, decorations
- Food/catering arrangements
- Parking and transportation
- Accessibility considerations
- Weather backup plan (if outdoor)
- Insurance requirements

**TEAM & ROLES:**
Suggested roles and responsibilities:
- Event coordinator
- Marketing lead
- Finance/treasurer
- Day-of logistics
- Registration/check-in
- etc.

**MARKETING PLAN:**
- When to announce
- Promotional channels
- Registration process
- Email campaigns
- Social media strategy
- Partner/sponsor outreach

**DAY-OF SCHEDULE:**
Sample timeline from setup to cleanup

**RISK MANAGEMENT:**
- What could go wrong?
- Contingency plans for each risk
- Emergency contacts needed

**POST-EVENT:**
- Thank you process
- Feedback collection
- Financial reconciliation
- Impact measurement
- Documentation for next time

Include specific action items for each phase.`,
          tags: ['event-planning', 'logistics', 'organization']
        },
        {
          title: 'Grant Application Package',
          content: `Help me apply for grants to fund [project/organization].

**ORGANIZATION:**
[Name and mission]

**PROJECT WE NEED FUNDING FOR:**
[Detailed description of what the money will support]

**AMOUNT NEEDED:**
$[amount]

**IMPACT:**
[Who benefits? How many people? What changes?]

**TIMELINE:**
[How long will this project/program run?]

**OUR QUALIFICATIONS:**
[Why are we the right people to do this? Track record? Expertise?]

---

Provide a complete grant application toolkit:

**PART 1: GRANT RESEARCH**
Help me identify:
- Types of grants we should target (foundation, corporate, government, community)
- Realistic grant size for our organization stage
- Key funders who support [our cause] in [our area]
- What funders look for in applications

**PART 2: ORGANIZATIONAL READINESS**
Checklist of what we need to have in place:
- Legal structure (501c3, etc.)
- Financial statements
- Board of directors
- Track record/proof of concept
- Budget processes
- Evaluation methods

**PART 3: CORE APPLICATION MATERIALS**
Draft templates for:

**Executive Summary:**
[2-paragraph compelling overview of organization and project]

**Need Statement:**
[Why this project is necessary - include data/evidence]

**Project Description:**
[Exactly what we'll do, when, and how]

**Goals & Objectives:**
[Specific, measurable outcomes]

**Evaluation Plan:**
[How we'll measure success]

**Organizational Capacity:**
[Why we can execute this successfully]

**Budget Narrative:**
[Detailed breakdown with justifications]

**Sustainability Plan:**
[How this continues beyond grant period]

**PART 4: BUDGET TEMPLATE**
Create a line-item budget showing:
- Personnel costs
- Program supplies
- Operational expenses
- Overhead (if allowed)
- Other funding sources (matching funds)

**PART 5: COMPELLING STORYTELLING**
Write:
- 3 powerful statistics about our impact/need
- 2 compelling beneficiary stories (anonymized)
- Our unique value proposition
- Testimonials or quotes (template for collecting)

**PART 6: APPLICATION STRATEGY**
- Typical timeline from research to submission
- How many grants to apply for simultaneously
- Follow-up process
- What to do if rejected
- Stewardship plan if funded

Make all writing clear, concise, and compelling for grant reviewers.`,
          tags: ['grant-writing', 'funding', 'nonprofit']
        },
        {
          title: 'Coding Project Architecture Plan',
          content: `Create a comprehensive technical architecture plan for my coding project.

**PROJECT OVERVIEW:**
- Project name: [name]
- What it does: [describe the main purpose/functionality in 2-3 sentences]
- Target users: [who will use this?]
- Platform: [web app, mobile app, desktop, API, etc.]

**KEY FEATURES:**
List the main features/functionality:
1. [Feature 1]
2. [Feature 2]
3. [Feature 3]
[Add more as needed]

**TECHNICAL CONSTRAINTS:**
- My skill level: [beginner/intermediate/advanced in what languages/frameworks?]
- Timeline: [how long do I have to build this?]
- Team size: [solo or X people]
- Budget: [free tools only / have some budget for hosting/services]

**REQUIREMENTS:**
- Expected users/traffic: [how many people will use this?]
- Data storage needs: [none/basic/extensive - what kind of data?]
- Security requirements: [user authentication? payment processing? sensitive data?]
- Performance needs: [real-time? fast response? background processing?]

---

Provide a complete architecture plan:

**PART 1: TECH STACK RECOMMENDATION**

**Frontend:**
- Framework/library: [React, Vue, vanilla JS, SwiftUI, etc.]
- Why this choice for my project and skill level
- Key libraries/packages to use
- Styling approach

**Backend:**
- Language/framework: [Node.js, Python/Django, Ruby/Rails, etc.]
- Why this choice
- API architecture (REST, GraphQL, etc.)
- Key libraries/packages

**Database:**
- Type: [SQL vs NoSQL - which and why]
- Specific database: [PostgreSQL, MongoDB, Firebase, etc.]
- Why this fits my data needs
- Schema design considerations

**Hosting/Deployment:**
- Where to host: [Vercel, Netlify, Heroku, AWS, etc.]
- Why this platform
- CI/CD approach
- Cost estimate

**Additional Services:**
- Authentication: [Firebase Auth, Auth0, JWT, etc.]
- File storage: [if needed]
- Email/notifications: [if needed]
- Payment processing: [if needed]
- Analytics: [if needed]

---

**PART 2: SYSTEM ARCHITECTURE DIAGRAM**

Describe the architecture in text:
1. User interface components
2. API endpoints needed
3. Database tables/collections
4. External services integration
5. Data flow between components

---

**PART 3: DATABASE SCHEMA**

Design the database structure:
- List all tables/collections needed
- Key fields for each
- Relationships between data
- Indexes needed for performance

---

**PART 4: API ENDPOINTS**

List all API routes needed:
- Endpoint URL
- HTTP method
- Purpose
- Request body/params
- Response format
- Authentication required?

---

**PART 5: FOLDER STRUCTURE**

Suggest project organization:

project-root/
├── frontend/
│   ├── src/
│   ├── components/
│   └── ...
├── backend/
│   ├── routes/
│   ├── controllers/
│   └── ...

---

**PART 6: DEVELOPMENT PHASES**

Break the project into phases:

**Phase 1: MVP (Minimum Viable Product)**
- Core features only
- What to build first
- Time estimate: X weeks

**Phase 2: Enhancement**
- Additional features
- What to add next
- Time estimate: X weeks

**Phase 3: Polish**
- UI improvements
- Performance optimization
- Testing

---

**PART 7: SECURITY CONSIDERATIONS**

List security measures needed:
- Authentication/authorization approach
- Data validation
- API security (rate limiting, CORS, etc.)
- Environment variables management
- Common vulnerabilities to avoid

---

**PART 8: SCALABILITY PLAN**

How will this handle growth?
- Caching strategy
- Database optimization
- Load balancing (if needed)
- When to worry about scale

---

**PART 9: TESTING STRATEGY**

What to test and how:
- Unit tests (what to test)
- Integration tests
- End-to-end tests
- Testing tools to use

---

**PART 10: GETTING STARTED CHECKLIST**

Concrete first steps:
- Set up development environment
- Initialize repositories
- Set up database
- Create basic project structure
- Install dependencies
- Configure environment variables
- Build hello world endpoint
- etc.

Make this practical and achievable for my skill level. Include links to key documentation/tutorials where helpful.`,
          tags: ['coding', 'architecture', 'technical']
        }
      ]
    },
      {
      id: 'studying',
      title: 'Studying',
      description: 'Effective study strategies, note-taking methods, and learning techniques',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      prompts: [
        {
          title: 'Study Plan Creator',
          content: 'Create a study plan for my upcoming [exam/test] on [date]. Topics to cover: [list topics]. I have [X] hours per day to study. Include spaced repetition and active recall techniques.',
          tags: ['planning', 'schedule', 'exam-prep']
        },
        {
          title: 'Concept Explanation',
          content: 'Explain [concept] from [subject] in simple terms. Then provide 3 real-world examples. Finally, give me 5 practice questions to test my understanding, with increasing difficulty.',
          tags: ['understanding', 'practice', 'concepts']
        },
        {
          title: 'Note Summarizer',
          content: 'Summarize these notes on [topic] using the Cornell method. Create main ideas, details, and a summary section. Include key terms I should memorize: [paste notes].',
          tags: ['notes', 'summarize', 'cornell']
        },
        {
          title: 'Flashcard Generator',
          content: 'Create 20 flashcards for [topic/chapter]. Include: key terms, definitions, important concepts, and memory aids. Format: question on one side, detailed answer on the other.',
          tags: ['flashcards', 'memorization', 'review']
        },
        {
          title: 'Study Group Questions',
          content: 'Generate 10 discussion questions for my study group covering [topic/chapter]. Range from basic recall to higher-level analysis questions. Include answer hints.',
          tags: ['group-study', 'discussion', 'collaboration']
        },
        {
          title: 'Memory Palace Technique',
          content: 'Help me use the memory palace technique to remember [list of items/concepts]. Create a vivid mental journey through [familiar location] with memorable associations for each item.',
          tags: ['memorization', 'technique', 'creative']
        }
      ]
    },
    
   
    {
      id: 'sat-act',
      title: 'SAT | ACT ',
      description: 'Test preparation strategies, practice, and score improvement tips',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      prompts: [
        {
          title: 'SAT Reading Strategy',
          content: 'I struggle with [specific SAT reading question type]. Explain strategies for approaching these questions efficiently. Provide 3 practice examples with step-by-step solutions.',
          tags: ['SAT', 'reading', 'strategy']
        },
        {
          title: 'Math Problem Breakdown',
          content: 'Explain how to solve this [SAT/ACT] math problem: [paste problem]. Break down the solution step-by-step, explain the concepts involved, and show me similar problems to practice.',
          tags: ['math', 'problem-solving', 'concepts']
        },
        {
          title: 'Math Practice Generator',
          content: 'I need to practice this type of math problem. Here is the original question:\n\n[Paste the complete SAT/ACT math problem here]\n\nFirst, solve this problem and show me the answer with a brief explanation.\n\nThen, create 3 similar practice problems that test the same concept but with different numbers. Make sure the problems have the same difficulty level and structure. Provide the answer key for all 3 practice problems at the end.',
          tags: ['math', 'practice', 'repetition']
        },
        {
          title: 'Essay Writing Template',
          content: 'Create a template for the [SAT/ACT] essay. Include: how to analyze the prompt, structure (intro, body, conclusion), time management tips, and transition phrases I can use.',
          tags: ['essay', 'writing', 'template']
        },
        {
          title: 'Grammar Rules Review',
          content: 'Review the most commonly tested grammar rules on the [SAT/ACT] writing section. For each rule, provide: the concept, how to identify it, common mistakes, and 2 practice examples.',
          tags: ['grammar', 'writing', 'rules']
        },
        {
          title: 'Test Day Strategy',
          content: 'Create a comprehensive test day strategy for the [SAT/ACT]. Include: timing for each section, when to skip/guess, stress management techniques, and how to maximize my score of around [current score].',
          tags: ['strategy', 'timing', 'test-day']
        },
        {
          title: 'Score Improvement Plan',
          content: 'My current [SAT/ACT] score is [score] and my goal is [target score]. My weakest sections are [sections]. Create a 6-week study plan with specific daily tasks to improve my weak areas.',
          tags: ['improvement', 'planning', 'goals']
        }
      ]
    },
    {
      id: 'ap-history',
      title: 'AP | History',
      description: 'AP US, World, and European History essay help and analysis',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      prompts: [
        {
          title: 'AP History Essay Outline',
          content: 'Create an outline for an AP [US/World/Euro] History [LEQ/DBQ] essay on [topic]. Thesis statement, 3 body paragraphs with evidence, and contextualization. Theme: [historical theme].',
          tags: ['AP', 'history', 'essay']
        },
        {
          title: 'Historical Comparison',
          content: 'Compare and contrast [historical event/period 1] with [historical event/period 2]. Analyze: causes, key figures, outcomes, and lasting impacts. Use this for my AP History [exam/essay].',
          tags: ['history', 'comparison', 'analysis']
        },
        {
          title: 'DBQ Document Analysis',
          content: 'Help me analyze these DBQ documents for [topic]: [paste documents]. For each document, identify: point of view, purpose, historical context, and audience. Organize them into categories that support my thesis: [thesis].',
          tags: ['DBQ', 'analysis', 'documents']
        },
        {
          title: 'DBQ Grader',
          content: `You are an experienced AP History teacher grading a DBQ essay. Evaluate this essay using the official rubric, then provide specific feedback and actionable suggestions for improvement.

---

**THE DBQ PROMPT:**
[Paste the DBQ prompt/question here]

**THE DOCUMENTS PROVIDED:**
Document 1: [Brief description - e.g., "Letter from Spanish conquistador to King Charles V, 1521"]
Document 2: [Brief description]
Document 3: [Brief description]
Document 4: [Brief description]
Document 5: [Brief description]
Document 6: [Brief description]
Document 7: [Brief description]

**STUDENT'S ESSAY:**
[Paste the complete essay here]

---

## GRADING TASK:

Evaluate this essay on 5 criteria (each scored 1-4). Then provide detailed feedback with examples and concrete suggestions.

**SCORING GUIDE:**
- **Advanced (4)**: Exceeds expectations, AP-ready
- **Proficient (3)**: Meets expectations with minor gaps
- **Developing (2)**: Partially meets expectations, needs significant work
- **Emerging (1)**: Does not meet expectations

---

## YOUR EVALUATION:

### SCORE BREAKDOWN

**1. THESIS/CLAIM: ___/4**

The student's thesis: "[Quote the thesis exactly]"

Score: [1, 2, 3, or 4]

Why this score:
- [Explain whether it's historically defensible, responds to prompt, and has a line of reasoning]

What's working:
- [Positive feedback]

What needs improvement:
- [Specific issues]

Suggestion for stronger thesis:
"[Write an improved version that includes a clear line of reasoning]"

---

**2. CONTEXTUALIZATION: ___/4**

The student's contextualization attempt: "[Quote relevant section or write 'Not present']"

Score: [1, 2, 3, or 4]

Why this score:
- [Is there broader historical context? Is it relevant? Is it substantial?]

What's working:
- [Positive feedback if applicable]

What needs improvement:
- [Specific gaps]

Suggestion:
"Here's an example of effective contextualization for this prompt: [Provide 2-3 sentence example connecting to broader historical events, developments, or processes before/during/after the time period]"

---

**3. EVIDENCE: ___/4**

Documents used: [List which documents were referenced - e.g., "Docs 1, 2, 4, 5"]

Outside evidence: [Quote any outside evidence provided, or write "None present"]

Score: [1, 2, 3, or 4]

Why this score:
- [Did they describe (not just quote) 4+ documents? Did they include outside evidence? Did evidence support an argument?]

What's working:
- [Positive feedback on evidence use]

What needs improvement:
- [Which documents were missing? Was evidence just cited or actually used to support arguments?]

Suggestions:
- Missing documents: "You didn't use Document [X]. Here's how you could incorporate it: [Give specific example]"
- Outside evidence: "Here's an example of relevant outside evidence you could add: [Provide specific historical fact/event/person related to the topic]"
- Better integration: "Instead of just quoting Document [X], try describing it like this: [Show example of description supporting an argument]"

---

**4. SOURCING (HIPP): ___/4**

Sourcing attempts found: [Quote where student tried to analyze Historical situation, Intended audience, Purpose, or Point of view]

Score: [1, 2, 3, or 4]

Why this score:
- [Did they source 2+ documents? Did they explain HOW/WHY the sourcing matters to their argument?]

What's working:
- [Positive feedback on any successful sourcing]

What needs improvement:
- [Identify weak or missing sourcing]

Suggestions:
"Strengthen your sourcing by explaining WHY it matters to your argument. For example:

WEAK: 'Document 3 is biased because the author is a British merchant.'

STRONG: 'As a British merchant writing to investors (audience), the author of Document 3 likely exaggerated the profitability of the trade route (purpose) to secure additional funding, which suggests that actual profits may have been lower than described. This complicates the argument that trade was universally lucrative.'

Try this approach with Documents [X] and [Y]."

---

**5. COMPLEXITY: ___/4**

Evidence of complexity: [Quote sections where student attempts nuance, multiple perspectives, connections, etc., or write "Not present"]

Score: [1, 2, 3, or 4]

Why this score:
- [Did they demonstrate nuance? Explain multiple causes/effects? Make cross-period connections? Use all 7 docs effectively?]

What's working:
- [Positive feedback if applicable]

What needs improvement:
- [What's missing or underdeveloped?]

Suggestions for achieving complexity:
[Choose the most relevant suggestion based on the prompt]

Option A - Nuance: "Acknowledge counterarguments or tensions. For example: 'While Documents 1 and 3 suggest [X], Documents 4 and 6 reveal [Y], indicating that the reality was more complex than a simple [X] narrative.'"

Option B - Corroboration: "Use multiple documents together: 'Both Document 2 (from the perspective of [group A]) and Document 5 (from [group B]) agree that [X], despite their different positions, which strongly supports the claim that [Y].'"

Option C - Connections: "Make broader connections: 'This pattern of [X] in [time period] mirrors similar developments during [earlier/later period], suggesting a longer continuity of [historical process].'"

---

### OVERALL ASSESSMENT

**TOTAL SCORE: ___/20**

**AP Exam Equivalent: ~___/7 points**
[Note: Advanced (4s) in each category = 7/7 on AP exam]

---

**TOP 3 STRENGTHS:**
1. [Specific positive with example from essay]
2. [Specific positive with example from essay]
3. [Specific positive with example from essay]

**TOP 3 PRIORITIES FOR REVISION:**
1. [Most important fix with specific action step]
2. [Second priority with specific action step]
3. [Third priority with specific action step]

---

### REVISION ROADMAP

**Quick Wins** (Can fix in 15 minutes):
- [Specific, actionable task - e.g., "Add one sentence of contextualization in your intro connecting to the Protestant Reformation"]
- [Specific, actionable task]

**Medium Effort** (Needs 30-45 minutes):
- [Specific task - e.g., "Add HIPP analysis for Documents 2 and 5 explaining why their perspectives matter"]
- [Specific task]

**Bigger Revision** (Needs 1+ hour):
- [Specific task - e.g., "Restructure body paragraphs to use documents as evidence supporting your arguments rather than just summarizing them"]
- [Specific task]

---

### EXAMPLE IMPROVEMENTS

**Your original paragraph:**
"[Quote a weak paragraph from their essay]"

**Revised version with improvements:**
"[Rewrite the paragraph showing better evidence use, sourcing, or complexity]"

**What changed:**
- [Explain the specific improvements made]

---

### QUESTIONS TO CONSIDER FOR NEXT DRAFT:

1. [Thought-provoking question to deepen their analysis - e.g., "Why might documents from [Group A] emphasize [X] while documents from [Group B] emphasize [Y]?"]
2. [Another analytical question]
3. [Another analytical question]

---

**FINAL ENCOURAGEMENT:**
[Brief, personalized comment acknowledging effort and highlighting most promising aspect of the essay]`,
          tags: ['DBQ', 'grading', 'feedback']
        }
      ]
    },
    {
      id: 'ap-english',
      title: 'AP | English',
      description: 'Literary analysis, rhetoric, poetry, and composition strategies',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      prompts: [
        {
          title: 'Sophistication Point Trainer',
          content: `Teach me how to earn the sophistication point on AP English essays through interactive practice.

**STEP 1: TEACH ME**
First, explain what the sophistication point is and the 4 main ways to earn it:
1. Exploring complexities/tensions
2. Illuminating interpretation through analysis
3. Employing a style that enhances the argument
4. Making effective rhetorical choices

**STEP 2: SHOW ME EXAMPLES**
Generate 4 different thesis statements responding to this prompt:
[Paste your essay prompt here]

For each thesis, label it as either:
- EARNS sophistication point (explain why)
- DOESN'T earn sophistication point (explain why)

**STEP 3: TEST MY UNDERSTANDING**
Now show me 5 MORE thesis statements (don't label them yet).

I'll analyze each one and tell you:
- Does it earn the sophistication point? (Yes/No)
- Why or why not?
- What could make it better?

**STEP 4: GIVE ME FEEDBACK**
After I respond, tell me:
- Which ones I got right/wrong
- Exactly what I'm missing in my understanding
- 3 concrete strategies I can use in my next essay

**STEP 5: APPLY TO MY ESSAY**
Here's my current thesis: [paste your thesis]

Help me revise it to earn the sophistication point.`,
          tags: ['sophistication', 'interactive', 'rubric']
        },
        {
          title: 'Thesis Surgery',
          content: `I need help making my thesis statement stronger and more sophisticated.

**MY THESIS:**
[Paste your thesis here]

**THE PROMPT:**
[Paste the essay prompt]

**WHAT I'M TRYING TO ARGUE:**
[Briefly explain your main point in your own words]

---

## YOUR TASK:

**DIAGNOSIS:**
Analyze my thesis and identify:
1. What's the line of reasoning? (Is it clear?)
2. Is it defensible and specific?
3. Does it actually answer the prompt?
4. What's missing or unclear?

**TREATMENT OPTIONS:**
Show me 3 different revised versions of my thesis:

**Version A - Add Complexity:**
[Rewrite to acknowledge tensions/nuances]
Why this works: [Explain]

**Version B - Sharpen the Reasoning:**
[Rewrite with clearer logical progression]
Why this works: [Explain]

**Version C - Elevate the Language:**
[Rewrite with more sophisticated structure/vocabulary]
Why this works: [Explain]

**HELP ME CHOOSE:**
Which version fits best with the argument I'm trying to make? Why?

**COMMON PITFALLS:**
Based on my original thesis, what are the top 3 traps I should avoid in the rest of my essay?`,
          tags: ['thesis', 'revision', 'feedback']
        },
        {
          title: 'Line of Reasoning Debugger',
          content: `Help me build a clear line of reasoning through my essay from start to finish.

**THE PROMPT:**
[Paste prompt]

**MY CLAIM:**
[What I'm arguing]

**MY EVIDENCE (in order I plan to use it):**
1. [First piece of evidence/example]
2. [Second piece of evidence/example]
3. [Third piece of evidence/example]
[Add more as needed]

---

## YOUR TASK:

**TEST 1: The "Because" Test**
Turn my claim into a chain of "because" statements. Does my evidence actually support each step?

Example:
"I claim X because Y, and Y is true because [Evidence 1] shows Z, and Z leads to X because..."

Do this for my argument. Show me where the logic breaks down.

**TEST 2: The "So What?" Test**
For each piece of evidence I listed, ask: "So what? Why does this matter to your argument?"
If I can't answer, my reasoning has a gap.

**TEST 3: The Order Test**
Is my evidence in the most logical order? Suggest a different order if mine doesn't build effectively.

**REBUILD:**
Based on these tests, show me:
1. A clear topic sentence for each body paragraph that connects to my thesis
2. How each piece of evidence should connect to the next
3. What commentary/analysis I need between evidence to make the reasoning clear
4. A reverse outline: what each paragraph accomplishes in my line of reasoning

**WARNING SIGNS:**
Point out any moments where my reasoning will seem like a "leap" to readers. Tell me what bridge I need to build.`,
          tags: ['reasoning', 'structure', 'logic']
        },
        {
          title: 'Rhetoric Analysis Decoder',
          content: `I need to analyze rhetorical strategies, but I struggle to go beyond just identifying them. Teach me to explain WHY they matter.

**THE PASSAGE:**
[Paste the rhetorical analysis passage]

**THE PROMPT:**
[Paste the prompt - usually asks about author's argument, purpose, or rhetorical choices]

---

## PHASE 1: BUILD THE FOUNDATION

Help me answer these before I start:
1. What is the author's PRIMARY argument? (One sentence)
2. Who is the intended audience? (Be specific - not just "everyone")
3. What does the author want the audience to think/feel/do?
4. What's the rhetorical situation (context, occasion, larger conversation)?

---

## PHASE 2: STRATEGY IDENTIFICATION

I've identified these rhetorical strategies: [list them - e.g., "metaphor in paragraph 2, anaphora in paragraph 4, emotional appeal in conclusion"]

For EACH strategy I listed, ask me:
- WHERE exactly is it? (Quote it)
- WHAT is it doing? (Description)
- HOW does it work on the audience?
- WHY did the author choose THIS strategy for THIS audience/purpose?

If my answer to "why" is weak, push back and help me dig deeper.

---

## PHASE 3: THE "SO WHAT" CHALLENGE

Transform my basic observations into sophisticated analysis.

For example:
BASIC: "The author uses a metaphor comparing freedom to a bird."
SOPHISTICATED: "By comparing freedom to a bird's unfettered flight, the author taps into the audience's desire for autonomy while simultaneously suggesting that freedom, like a bird, is both beautiful and fragile—a duality that complicates simplistic notions of liberty."

Take 3 of my identified strategies and show me the basic vs. sophisticated version.

---

## PHASE 4: BUILD MY ESSAY STRUCTURE

Suggest:
1. A thesis that makes a claim about HOW the rhetorical choices achieve the author's purpose
2. 3 body paragraph topics organized by effect/purpose (not just "paragraph 1 has metaphors, paragraph 2 has anaphora")
3. Specific quotes and the analysis needed for each

---

**FINAL CHECK:**
Am I analyzing rhetoric or just summarizing what the author said? How can I make sure every sentence of my essay is focused on HOW the author persuades?`,
          tags: ['rhetoric', 'analysis', 'AP-Lang']
        },
        {
          title: 'Poetry Analysis Unlocker',
          content: `Help me unlock the deeper meaning of this poem beyond surface-level observations.

**THE POEM:**
[Paste the complete poem]

**THE PROMPT (if applicable):**
[Paste prompt, or write "Open analysis"]

---

## ROUND 1: INITIAL IMPRESSIONS

First, tell me what you notice:
- What's the poem literally about? (Plot/situation)
- What mood/feeling does it create?
- Any words or images that jump out?

Don't analyze yet - just observe.

---

## ROUND 2: CLOSE READING LAYER 1 - SOUND & STRUCTURE

Analyze these technical elements AND explain what they contribute to meaning:

1. **Form:** [Sonnet? Free verse? Stanzas?] → Why did the poet choose this structure?
2. **Rhyme scheme:** [Pattern?] → What effect does it create?
3. **Meter/Rhythm:** [Regular? Irregular?] → Where does it break? Why?
4. **Sound devices:** [Alliteration, assonance, consonance?] → What mood do they create?
5. **Line breaks:** Where does the poet break lines? What emphasis does this create?

For each element, explain: "This choice makes me feel/think _____ which connects to the poem's larger meaning of _____."

---

## ROUND 3: CLOSE READING LAYER 2 - LANGUAGE & IMAGERY

Analyze the most important language choices:

1. Pick the 3 most loaded/interesting words. Why these words instead of synonyms?
2. What's the central image or metaphor? How does it develop through the poem?
3. What senses does the poet appeal to? Why these senses?
4. Where is the language simple vs. complex? Why the shift?
5. Any ambiguity or words with double meanings? Why include ambiguity?

---

## ROUND 4: THE TURN

Every good poem has a turn (volta) - a shift in thought, image, or tone.

Where is the turn in this poem? (Look around line 8 in sonnets, or between stanzas in other forms)

What changes at the turn?
- The imagery?
- The speaker's realization?
- The tone?

Why is this turn significant to the overall meaning?

---

## ROUND 5: SYNTHESIS

Now that we've analyzed the parts, help me articulate the whole:

1. What is this poem REALLY about? (Beyond the surface)
2. Write a thesis statement that makes a sophisticated claim about how the poet's choices create meaning
3. Show me how to write ONE body paragraph that:
   - Makes a subclaim
   - Quotes the poem strategically
   - Analyzes language, form, and sound TOGETHER
   - Connects back to the larger meaning

---

**SOPHISTICATION BOOST:**
What complexity, ambiguity, or tension can I explore to elevate my analysis beyond the obvious?`,
          tags: ['poetry', 'close-reading', 'analysis']
        },
        {
          title: 'Evidence Integration Masterclass',
          content: `I keep just dropping quotes into my essay without integrating them well. Teach me how to seamlessly weave evidence into my analysis.

**MY ESSAY EXCERPT WITH EVIDENCE:**
[Paste a paragraph or two from your essay that includes quotes]

**THE PROMPT I'M RESPONDING TO:**
[Paste prompt]

---

## DIAGNOSIS: What's Wrong With My Evidence Use?

Evaluate my excerpt for these common problems:
- "Quote dropping" (plopping quotes in without introduction)
- Too much plot summary instead of analysis
- Quotes that are too long
- Missing the key words/phrases in the quote
- Not explaining HOW the evidence proves the claim
- Evidence doesn't actually support the point

For each problem you find, show me the specific sentence/quote and explain the issue.

---

## THE INTEGRATION FORMULA

Teach me the pattern: **INTRODUCE → QUOTE → ANALYZE → CONNECT**

Take ONE of my problematic quotes and rewrite it using this formula:

**INTRODUCE (Set up the quote):**
- Name the author/speaker/context
- Preview what the quote will show
Example: "Smith establishes the central tension when she describes..."

**QUOTE (Strategic selection):**
- Use only the essential words
- Integrate grammatically into your sentence
- Use [...] for omissions, [brackets] for clarifications

**ANALYZE (Explain the language):**
- Zoom in on specific word choices
- Explain connotations, implications, literary devices
- Connect the HOW to the WHY

**CONNECT (Link back to thesis):**
- Explicitly state how this evidence proves your claim
- Use phrases like "This reveals," "Thus," "Consequently"

---

## PRACTICE: Rewrite 3 of My Quotes

Take 3 quotes from my excerpt and show me:
1. My original version (point out what's weak)
2. Improved version using the integration formula
3. Why the improved version is stronger

---

## ADVANCED MOVES:

Show me examples of:

**Move 1: The Embed**
Weaving short quotes (1-3 words) directly into your sentence
Example: The speaker's use of "shattered" rather than simply "broken" emphasizes...

**Move 2: The Setup Sandwich**
Your interpretation + quote + extension of interpretation
Example: While the character initially appears confident, her "trembling hands betrayed the fear she tried to hide," revealing the gap between performance and reality.

**Move 3: The Strategic Ellipsis**
Cutting out unnecessary words to focus on what matters
Example: Instead of: "And so it goes, and so it goes, and so it will continue to go forever"
Use: "And so it goes [...] and so it will continue [...] forever"

---

## FINAL POLISH:

Rewrite my entire excerpt with properly integrated evidence. Then explain:
1. What changed?
2. How is the analysis stronger?
3. What pattern should I follow in the rest of my essay?`,
          tags: ['evidence', 'integration', 'writing']
        }
      ]
    },
    {
      id: 'ap-science',
      title: 'AP | Science',
      description: 'Biology, Chemistry, Physics lab reports and concepts',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      prompts: [
        {
          title: 'Science Lab Report',
          content: 'Help me write a lab report for [experiment name] in [AP/regular] [subject]. Sections needed: hypothesis, procedure, data analysis, conclusion. Key findings: [your findings].',
          tags: ['science', 'lab', 'report']
        },
        {
          title: 'Biology Study Guide',
          content: 'Create a comprehensive study guide for [AP/regular] Biology chapter on [topic]. Include: key terms, processes, diagrams to memorize, common exam questions, and mnemonic devices.',
          tags: ['biology', 'study-guide', 'memorization']
        },
        {
          title: 'Chemistry Problem Solver',
          content: 'Solve this chemistry problem: [paste problem]. Show dimensional analysis, significant figures, and explain the underlying concepts. Then give me 3 similar problems to practice.',
          tags: ['chemistry', 'problem-solving', 'practice']
        }
      ]
    },
    {
      id: 'ap-math',
      title: 'AP | Math',
      description: 'Calculus, Statistics, and advanced mathematics',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      prompts: [
        {
          title: 'Math Concept Mastery',
          content: 'Teach me [math concept] from [AP Calculus/Statistics/other]. Start with basics, build to advanced applications. Include common mistakes to avoid and 5 practice problems with solutions.',
          tags: ['math', 'concepts', 'practice']
        },
        {
          title: 'Calculus Problem Walkthrough',
          content: 'Walk me through this calculus problem step-by-step: [paste problem]. Explain which rules/theorems to use, show all work, and explain why each step is necessary.',
          tags: ['calculus', 'problem-solving', 'steps']
        }
      ]
    }
  ];

  useEffect(() => {
    const handleOpenCategory = (event) => {
      const categoryId = event.detail;
      const category = mainCategories.find(cat => cat.id === categoryId);
      if (category) {
        setSelectedCategory(category);
        setExpandedPrompts({});
        // Dispatch event for sidebar to update
        window.dispatchEvent(new CustomEvent('promptCategorySelected', { detail: categoryId }));
      }
    };

    window.addEventListener('openPromptCategory', handleOpenCategory);
    return () => window.removeEventListener('openPromptCategory', handleOpenCategory);
  }, [mainCategories]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setExpandedPrompts({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Dispatch event for sidebar to update
    window.dispatchEvent(new CustomEvent('promptCategorySelected', { detail: category.id }));
  };

  const handleBack = () => {
    setSelectedCategory(null);
    setCopiedIndex(null);
    setExpandedPrompts({});
    // Dispatch event for sidebar to clear selection
    window.dispatchEvent(new CustomEvent('promptCategorySelected', { detail: null }));
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  const toggleExpand = (index) => {
    setExpandedPrompts(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getPreviewContent = (content) => {
    const lines = content.split('\n');
    return lines.slice(0, 22).join('\n');
  };

  const CategoryCard = ({ category }) => {
    const [isHovered, setIsHovered] = React.useState(false);
    
    return (
      <div
        onClick={() => handleCategoryClick(category)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          border: isHovered ? '3px solid transparent' : '3px solid #ddd',
          backgroundImage: isHovered ? `linear-gradient(white, white), ${category.gradient}` : 'none',
          backgroundOrigin: 'border-box',
          backgroundClip: isHovered ? 'padding-box, border-box' : 'padding-box',
          transform: isHovered ? 'translateY(-4px)' : 'translateY(0)'
        }}
      >
        <h2 style={{
          fontSize: '1.8rem',
          fontWeight: 'bold',
          marginBottom: '0.75rem',
          margin: 0,
          backgroundImage: category.gradient,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          {category.title}
        </h2>
        
        <p style={{
          fontSize: '0.95rem',
          lineHeight: '1.5',
          color: '#6c757d',
          marginBottom: '1rem'
        }}>
          {category.description}
        </p>
        
      <div style={{
          fontSize: '0.9rem',
          color: '#495057',
          fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
          gap: '0.5rem'
        }}>
          {category.prompts.length} prompts
          <ArrowRight size={16} />
        </div>
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'white',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: selectedCategory ? '900px' : '800px',
        margin: '0 auto'
      }}>
        {!selectedCategory ? (
          <>
        <h1 style={{
              textAlign: 'left',
          color: '#2c3e50',
              marginBottom: '1rem',
              fontSize: '3rem',
              fontWeight: 'bold'
        }}>
              AI Prompt Library
        </h1>

            <p style={{
              textAlign: 'left',
              color: '#6c757d',
              marginBottom: '3rem',
              fontSize: '1.2rem',
              maxWidth: '700px'
            }}>
              Select a category below to access curated AI prompts for your academic needs
            </p>
            
            {/* All Categories Grid */}
        <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1.5rem'
            }}>
              {mainCategories.map(category => (
                <CategoryCard key={category.id} category={category} />
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Back Button and Header */}
            <button
              onClick={handleBack}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#6c757d',
              fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                padding: '0.5rem 0',
                marginBottom: '2rem',
                transition: 'color 0.3s'
              }}
              onMouseEnter={(e) => e.target.style.color = '#2c3e50'}
              onMouseLeave={(e) => e.target.style.color = '#6c757d'}
            >
              <ArrowLeft size={20} />
              Back to Categories
            </button>

          <div style={{
              marginBottom: '2rem',
              paddingBottom: '2rem',
              borderBottom: '2px solid #e9ecef'
            }}>
              <h1 style={{
                margin: 0,
                fontSize: '2.5rem',
                fontWeight: 'bold',
                marginBottom: '1.5rem',
                backgroundImage: selectedCategory.gradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                display: 'inline-block'
              }}>
                {selectedCategory.title}
              </h1>
              
              <p style={{
                margin: 0,
                fontSize: '1.1rem',
                color: '#6c757d'
              }}>
                {selectedCategory.description}
              </p>
          </div>

            {/* Prompts List */}
            <div>
          <div style={{
                display: 'flex',
                flexDirection: 'column'
              }}>
                {selectedCategory.prompts.map((prompt, index) => (
                  <div key={index} style={{
                    marginBottom: index < selectedCategory.prompts.length - 1 ? '3.5rem' : '0'
                  }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                      alignItems: 'center',
                  marginBottom: '1rem'
                }}>
                  <h3 style={{
                    color: '#2c3e50',
                    margin: 0,
                        fontSize: '1.4rem',
                        fontWeight: 'bold',
                        flex: 1
                  }}>
                    {prompt.title}
                  </h3>
                      
                      <button
                        onClick={() => copyToClipboard(prompt.content, index)}
                        style={{
                          backgroundColor: 'transparent',
                          color: '#000',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 1rem',
                          borderRadius: '4px',
                          transition: 'background-color 0.3s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <Copy size={16} />
                        {copiedIndex === index ? 'Copied!' : 'Copy'}
                      </button>
                </div>
                
                    <div style={{
                      backgroundColor: '#fafafa',
                      border: '1px solid #e5e5e5',
                      borderRadius: '8px',
                      padding: '1.5rem',
                  marginBottom: '1rem'
                }}>
                      <pre style={{
                        margin: 0,
                        fontSize: '0.95rem',
                        lineHeight: '1.6',
                        color: '#2c3e50',
                        fontFamily: '"Space Mono", monospace',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word'
                      }}>
                        {expandedPrompts[index] ? prompt.content : getPreviewContent(prompt.content)}
                      </pre>
                    </div>
                    
                    {prompt.content.split('\n').length > 22 && (
                      <button
                        onClick={() => toggleExpand(index)}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#6c757d',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 0',
                          transition: 'color 0.3s'
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#2c3e50'}
                        onMouseLeave={(e) => e.target.style.color = '#6c757d'}
                      >
                        {expandedPrompts[index] ? (
                          <>
                            Show Less
                            <ChevronUp size={16} />
                          </>
                        ) : (
                          <>
                            Show More
                            <ChevronDown size={16} />
                          </>
                        )}
                      </button>
                    )}
              </div>
            ))}
          </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PromptLibrary;

